import { createFileRoute } from "@tanstack/react-router";
import {
  Atom,
  BookOpen,
  Brain,
  ClipboardList,
  FlaskConical,
  LineChart,
  RotateCcw,
  Wrench,
} from "lucide-react";
import { seo } from "@/lib/seo";
import {
  ClosingCta,
  FeatureCard,
  MarketingPage,
  MarketingSection,
  StartFreeButton,
} from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/features")({
  head: () =>
    seo({
      title: "Features — JEE Syllabus, Revision, Test & Question Tracking | The Quantum Mind",
      description:
        "Every feature of The Quantum Mind: JEE syllabus tracker, spaced revision planner, test tracker with PCM scores, question practice targets, simulations, virtual labs and a study toolkit.",
      path: "/features",
    }),
  component: FeaturesPage,
});

const GROUPS = [
  {
    icon: BookOpen,
    title: "Syllabus & lecture tracking",
    body: "Add chapters with their lecture counts, tick lectures as you complete them and get a real completion percentage per subject.",
  },
  {
    icon: RotateCcw,
    title: "Revision planner",
    body: "Spaced repetition schedules each completed lecture for repeat revision. Missed revisions carry forward instead of disappearing.",
  },
  {
    icon: ClipboardList,
    title: "Test tracker",
    body: "Create a test with its date and get a day-by-day revision and practice schedule. Enter Physics, Chemistry and Maths marks on test day.",
  },
  {
    icon: LineChart,
    title: "Performance analytics",
    body: "Attempted tests, average, highest and lowest scores, improvement trend, subject-wise percentages and full score history.",
  },
  {
    icon: Brain,
    title: "Question practice",
    body: "Set question targets per chapter, log what you actually solved and see which chapters are under-practised.",
  },
  {
    icon: Atom,
    title: "Simulations",
    body: "Interactive physics and chemistry simulations that make abstract concepts visible and adjustable.",
  },
  {
    icon: FlaskConical,
    title: "Virtual labs",
    body: "Pendulums, optics benches, circuits, calorimetry and more — full experiments with no equipment needed.",
  },
  {
    icon: Wrench,
    title: "Study toolkit",
    body: "Periodic table, physical constants, formula and equation sheets, unit converters, calculators, flashcards and quizzes.",
  },
];

function FeaturesPage() {
  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Features
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
        Everything The Quantum Mind tracks for your JEE preparation
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
        One system for syllabus, lectures, revision, question practice, tests and analytics —
        plus simulations, virtual labs and a full study toolkit. Browse freely; sign up when you
        want your progress saved and synced.
      </p>
      <StartFreeButton className="mt-6" label="Start free" />

      <MarketingSection title="Core tracking">
        <div className="grid gap-4 sm:grid-cols-2">
          {GROUPS.map((g) => (
            <FeatureCard key={g.title} {...g} />
          ))}
        </div>
      </MarketingSection>

      <ClosingCta />
    </MarketingPage>
  );
}
